// PURE tap-select then tap-target (IMPLEMENTATION §2.7 "Tap semantics" / "Drop resolution order", UX §3.7).
// A tapped loose item is selected (it lifts, valid targets glow); the next tap on a target runs the SAME
// resolver as a drop (query.resolveDrop). Differences from dragging: tapping a different item changes the
// selection (never a swap), an invalid target shows its reason and keeps the selection, tapping empty
// floor clears it, and tapping the selected item again deselects after doubleTapMs (within it, a double tap
// is Quick Merge once unlocked). Tap-placement from the Shop is a selection too: tapping a cell buys.
import type { Dst, RejectReason, Src } from '../sim/commands.ts';
import type { ShopId } from '../sim/gen/ids.ts';
import type { DropOutcome } from '../sim/query.ts';
import type { ZoneId } from './dropZones.ts';

export type Selection =
  | { k: 'item'; src: Extract<Src, { k: 'cell' }>; t: number }
  | { k: 'shop'; item: ShopId; t: number };

export type TapTarget =
  | { k: 'item'; id: number; x: number; y: number }
  | { k: 'empty'; x: number; y: number }
  | { k: 'building'; id: number; x: number; y: number }
  | { k: 'rock'; x: number; y: number }
  | { k: 'zone'; zone: ZoneId }
  | { k: 'none' };

export type TapAction =
  /** Select a loose item (a new selection or a changed one). */
  | { k: 'select'; sel: Selection }
  | { k: 'clear' }
  /** Run `drop` from the selection (the resolver said it would do something). */
  | { k: 'drop'; from: Src; to: Dst }
  /** Invalid target: reason bubble, keep the selection. */
  | { k: 'refuse'; reason: RejectReason }
  | { k: 'quickMerge'; id: number }
  | { k: 'keep' }
  /** No selection is involved: the caller's own tap semantics apply (cards, tools, …). */
  | { k: 'pass' };

export interface TapCtx {
  t: number;
  /** This tap is the 2nd of a double tap on the same object. */
  double: boolean;
  quickMergeOn: boolean;
  doubleTapMs: number;
  /** The resolver's outcome for a drop of the selection onto `to` (query.resolveDrop). */
  preview(from: Src, to: Dst): DropOutcome;
}

function dstOf(t: TapTarget): Dst | null {
  switch (t.k) {
    case 'item':
    case 'empty':
    case 'building':
    case 'rock':
      return { k: 'cell', x: t.x, y: t.y };
    case 'zone':
      if (t.zone === 'counter') return { k: 'counter' };
      if (t.zone === 'sell') return { k: 'sell' };
      return null; // the peek cancels; the gun slot is registered with its building by the card (v0.2)
    case 'none':
      return null;
  }
}

function srcOf(sel: Selection): Src {
  return sel.k === 'item' ? sel.src : { k: 'shop', item: sel.item };
}

export function selectionTap(sel: Selection | null, target: TapTarget, c: TapCtx): TapAction {
  // the 2nd tap of a double tap on a loose item: Quick Merge (the 1st tap already selected it); a waiting
  // Shop tap-placement takes every board tap (M6 decision 5), a quick 2nd tap included
  if (target.k === 'item' && c.double && c.quickMergeOn && sel?.k !== 'shop')
    return { k: 'quickMerge', id: target.id };
  if (sel === null) {
    if (target.k === 'item')
      return {
        k: 'select',
        sel: { k: 'item', src: { k: 'cell', x: target.x, y: target.y, id: target.id }, t: c.t },
      };
    return { k: 'pass' };
  }
  if (sel.k === 'item' && target.k === 'item' && target.id === sel.src.id) {
    return c.t - sel.t > c.doubleTapMs ? { k: 'clear' } : { k: 'keep' };
  }
  if (target.k === 'none') return { k: 'clear' };
  if (target.k === 'zone' && target.zone === 'peek') return { k: 'clear' };
  if (sel.k === 'item' && target.k === 'empty') return { k: 'clear' };
  const to = dstOf(target);
  if (to === null) return { k: 'clear' };
  const from = srcOf(sel);
  const out = c.preview(from, to);
  if (sel.k === 'item' && target.k === 'item') {
    // tap-select never swaps: only a merge acts; any other item becomes the selection
    if (out.k === 'merge') return { k: 'drop', from, to };
    return {
      k: 'select',
      sel: { k: 'item', src: { k: 'cell', x: target.x, y: target.y, id: target.id }, t: c.t },
    };
  }
  if (out.k === 'reject') return { k: 'refuse', reason: out.reason };
  if (out.k === 'none') return { k: 'keep' };
  return { k: 'drop', from, to };
}
